import {
  calculatePonderators,
  getRecursiveFromParentTask
} from '../../../../utils/lookahead-common';
import {
  findDeepGetTask,
  findFastGetActivity,
  isGrouped,
  updateAsyncTaskGanttV2
} from '../../GanttVisualization.helper';

const COLUMNS = {
  COST: 'cost',
  REAL_ENDOWMENT: 'real_endowment',
  PLAN_ENDOWMENT: 'plan_endowment'
};

const projectCurrencySymbols = Object.freeze({
  CLP: '$',
  UF: 'UF',
  USD: '$',
  EUR: 'Є',
  PEN: 'S/',
  ARS: '$',
  UYU: '$',
  BRL: 'R$',
  COP: '$',
  MXN: '$',
  BOB: 'BS',
  VES: 'Bs',
  GYD: '$',
  SRD: '$',
  TTD: '$',
  RP: 'Rp',
  CAD: 'C$',
  GBP: '£'
});

export function formatValue(value, column, t) {
  if (!value.toFixed) return '';
  const DECIMAL_PLACES = 2;

  let formattedValue = value;
  if (['real_endowment', 'plan_endowment'].includes(column?.name)) {
    formattedValue = Math.round(parseFloat(value));
  }

  return Number(
    formattedValue.toFixed(
      Number.isInteger(parseFloat(formattedValue)) ? 0 : DECIMAL_PLACES
    )
  ).toLocaleString(t('lang') === 'en' ? 'en-US' : 'es-ES', {
    useGrouping: true
  });
}

export const currencySymbolByProject = (project) => {
  const currencySymbol = projectCurrencySymbols[project.currency];
  if (!currencySymbol) {
    return '';
  }
  return currencySymbol;
};

export const shouldSkipRendering = (task, columnName) => {
  if (!task || task?.is_milestone || (columnName !== 'cost' && !task?.isTask)) {
    return true;
  }
  return false;
};

export const isTaskDisabledForNumeric = (task, columnName, column) => {
  const disableTask =
    (columnName === 'cost' && task.type === 'main') ||
    isGrouped() ||
    column?.calculated;
  task['disabledNumeric_' + columnName] = disableTask === true;
  return disableTask;
};

export const calculateAndUpdateTaskValues = (
  task,
  taskFromLookahead,
  columnName
) => {
  if (task.type !== 'main' && taskFromLookahead?.children?.length) {
    if (task[columnName] !== taskFromLookahead[columnName]) {
      getRecursiveFromParentTask(
        taskFromLookahead,
        columnName,
        () => null,
        () => null
      );
      task[columnName] = taskFromLookahead[columnName];
    }
  }
};

export const calculateExpectedCost = (
  task,
  assignValueFromGanttToLookaheadRef
) => {
  const PERCENTAGE_DIVISOR = 100;
  if (task.type !== 'activitytask') return null;
  const newExpectedCost = task.cost * (task.expected / PERCENTAGE_DIVISOR);
  if (task.expected_cost !== newExpectedCost) {
    assignValueFromGanttToLookaheadRef(task, newExpectedCost, 'expected_cost');
  }
  return newExpectedCost;
};

const updateAsyncParent = (idTask, columnName) => {
  const ganttObjectRef = window.ganttVisualization.getTask(idTask);

  const updateData = async (taskCallback) => {
    await updateAsyncTaskGanttV2(
      taskCallback,
      columnName,
      taskCallback[columnName],
      window.ganttVisualization,
      true,
      false,
      false,
      true
    );
  };
  if (ganttObjectRef.isTask) {
    const getReferenceTask = findDeepGetTask(
      window.activities,
      'id',
      ganttObjectRef.id
    );
    const taskRef = getReferenceTask || null;
    getRecursiveFromParentTask(taskRef, columnName, () => null, updateData);
    if (taskRef.parent_id) {
      updateAsyncParent(taskRef.parent_id, columnName);
    }
  }
};

const updateAsyncTask = async (taskLocal, columnName, taskFromLookahead) => {
  await updateAsyncTaskGanttV2(
    taskFromLookahead,
    columnName,
    taskLocal[columnName],
    window.ganttVisualization,
    true,
    false,
    false,
    true
  );

  if (taskFromLookahead.parent_id) {
    updateAsyncParent(taskFromLookahead.parent_id, columnName);
  }
};

const getTaskRelatedInfo = (activities, task) => {
  const activity = findFastGetActivity(
    activities,
    task?.activityReference?.proplannerId
  );
  const taskFromLookahead = findDeepGetTask(activities, 'id', task.id);
  const parent = taskFromLookahead?.parent_id
    ? findDeepGetTask(activities, 'id', taskFromLookahead.parent_id)
    : null;

  return {
    activity,
    taskFromLookahead,
    parent,
    originalTask: task
  };
};

const updateCostValues = (taskFromLookahead, parent, activity, newValue) => {
  taskFromLookahead.cost = newValue;
  if (parent) {
    parent.cost = newValue;
  }
  activity.tasks.forEach((task) => {
    if (task.id === taskFromLookahead.id) {
      task.cost = newValue;
    }
  });
};

const updatePonderators = async (targetEntity, gantt, projectState) => {
  calculatePonderators(
    targetEntity,
    null,
    (taskCallback) => {
      const ponderatorValue = parseFloat(taskCallback.ponderator);
      updateAsyncTaskGanttV2(
        taskCallback,
        'ponderator',
        ponderatorValue,
        gantt
      );
    },
    projectState
  );
};

const handleCostChange = async (taskInfo, newValue, gantt, projectState) => {
  const { activity, taskFromLookahead, parent, originalTask } = taskInfo;
  const project = projectState.allProjects.find(
    (project) => project.id === projectState.projectSelected
  );

  const isProjectConfiguredByCost =
    project?.task_creter && project.task_creter.toUpperCase() === 'COST';

  if (isProjectConfiguredByCost) {
    updateCostValues(taskFromLookahead, parent, activity, newValue);
    await updatePonderators(parent || activity, gantt, projectState);
  }

  originalTask[COLUMNS.COST] = newValue;
  await updateAsyncTask(originalTask, COLUMNS.COST, taskFromLookahead);
};

const handleOtherNumericChange = async (taskInfo, columnName, newValue) => {
  const { originalTask, taskFromLookahead } = taskInfo;
  let processedValue = newValue;

  if ([COLUMNS.REAL_ENDOWMENT, COLUMNS.PLAN_ENDOWMENT].includes(columnName)) {
    processedValue = Math.round(newValue);
  }

  originalTask[columnName] = processedValue;
  await updateAsyncTask(originalTask, columnName, taskFromLookahead);
};

export const onChangeNumeric = async (
  task,
  columnName,
  newValue,
  projectState
) => {
  const activities = window.activities;
  const gantt = window.ganttVisualization;

  const taskInfo = getTaskRelatedInfo(activities, task);

  if (columnName === COLUMNS.COST) {
    await handleCostChange(taskInfo, newValue, gantt, projectState);
  } else {
    await handleOtherNumericChange(taskInfo, columnName, newValue);
  }
};
